import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSupabaseQuery } from '../hooks/useSupabase';
import { ArrowLeft, BookOpen, Loader2 } from 'lucide-react';

export default function ApproachView() {
  const { number } = useParams<{ number: string }>();
  const approachNumber = parseInt(number || '1', 10);

  // Fetch all published approaches
  const { data: approaches } = useSupabaseQuery('approaches', {
    filter: (q: any) => q.eq('status', 'published'),
  });

  const approach = approaches?.find((a: any) => a.number === approachNumber);

  if (!approaches) {
    return (
      <div className="flex items-center justify-center h-full py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
      </div>
    );
  }

  if (!approach) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-8 text-center">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Tilnærming ikke funnet
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Tilnærming {approachNumber} finnes ikke eller er ikke publisert.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-white/20 rounded-lg">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{approach.name_no}</h1>
              <p className="text-blue-100 mt-1">Oppslagsverk for betjenter</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-8">
            {approach.description ? (
              <div
                className="prose prose-lg dark:prose-invert max-w-none
                  prose-headings:text-gray-900 dark:prose-headings:text-white
                  prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700 prose-h2:pb-2
                  prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed
                  prose-ul:my-4 prose-ul:space-y-2
                  prose-li:text-gray-700 dark:prose-li:text-gray-300
                  prose-strong:text-gray-900 dark:prose-strong:text-white
                  prose-em:text-gray-600 dark:prose-em:text-gray-400"
                dangerouslySetInnerHTML={{ __html: approach.description }}
              />
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">
                Ingen beskrivelse tilgjengelig for denne tilnærmingen.
              </p>
            )}
          </div>

          {/* Navigation between approaches */}
          <div className="mt-6 flex justify-between">
            {approachNumber > 1 && (
              <Link
                to={`/approaches/${approachNumber - 1}`}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                Forrige tilnærming
              </Link>
            )}
            <div className="flex-1" />
            {approaches && approachNumber < approaches.length && (
              <Link
                to={`/approaches/${approachNumber + 1}`}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
              >
                Neste tilnærming
                <ArrowLeft className="h-4 w-4 rotate-180" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
